import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Certificate, DownloadSimple, UploadSimple, CheckCircle,
  Archive, MagnifyingGlass, Eye,
} from "@phosphor-icons/react";

export default function AdminCOA() {
  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10 space-y-10" data-testid="admin-coa-page">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-2">Documentation</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">
              COA Management
            </h1>
            <p className="mt-2 text-brand-muted text-sm max-w-2xl">
              Upload, version, and publish Certificates of Analysis per SKU + lot number. PNG/JPG uploads are auto-converted to PDF. Every COA is preserved indefinitely — publishing a new one archives the previous.
            </p>
          </div>
        </header>

        <UploadCOACard onUploaded={() => window.dispatchEvent(new Event("coa-refresh"))} />
        <COAList />
      </div>
    </AdminGuard>
  );
}


// -----------------------------------------------------------------------------
// Upload card
// -----------------------------------------------------------------------------

function UploadCOACard({ onUploaded }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [form, setForm] = useState({
    lot_number: "",
    laboratory: "",
    test_date: "",
    issue_date: "",
    manufacture_date: "",
    expiration_date: "",
    purity_percent: "",
    research_grade: "Research Reference Standard",
    storage_conditions: "",
    notes_internal: "",
    publish: true,
  });
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/products").then((r) => setProducts(r.data));
  }, []);

  const productOptions = useMemo(
    () => [...products].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "")),
    [products],
  );

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const submit = async () => {
    if (!productId) { toast.error("Select a product"); return; }
    if (!file) { toast.error("Attach a COA file"); return; }
    if (!form.lot_number.trim()) { toast.error("Lot number is required"); return; }

    const fd = new FormData();
    fd.append("product_id", productId);
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    fd.append("file", file);

    setUploading(true);
    try {
      const token = localStorage.getItem("mps_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coa/direct-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast.success(`Uploaded ${data.download_filename} (v${data.version})`);
      setFile(null);
      setForm({
        lot_number: "", laboratory: "", test_date: "", issue_date: "",
        manufacture_date: "", expiration_date: "", purity_percent: "",
        research_grade: "Research Reference Standard",
        storage_conditions: "", notes_internal: "", publish: true,
      });
      onUploaded?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="border border-brand-border bg-white p-6" data-testid="admin-coa-upload-card">
      <div className="flex items-center gap-2 mb-4">
        <UploadSimple size={18} className="text-brand-accent" weight="duotone" />
        <h2 className="font-display text-xl font-semibold text-brand-navy">New Certificate of Analysis</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-muted">Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="h-10 mt-1" data-testid="admin-coa-product-select"><SelectValue placeholder="Select a SKU…" /></SelectTrigger>
              <SelectContent>
                {productOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lot Number *" value={form.lot_number} onChange={(v) => setForm({ ...form, lot_number: v })} testid="admin-coa-lot" />
            <Field label="Laboratory" value={form.laboratory} onChange={(v) => setForm({ ...form, laboratory: v })} testid="admin-coa-lab" />
            <Field label="Test Date" type="date" value={form.test_date} onChange={(v) => setForm({ ...form, test_date: v })} testid="admin-coa-testdate" />
            <Field label="Issue Date" type="date" value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testid="admin-coa-issuedate" />
            <Field label="Manufacture Date" type="date" value={form.manufacture_date} onChange={(v) => setForm({ ...form, manufacture_date: v })} testid="admin-coa-mfgdate" />
            <Field label="Expiration Date" type="date" value={form.expiration_date} onChange={(v) => setForm({ ...form, expiration_date: v })} testid="admin-coa-expdate" />
            <Field label="Purity %" value={form.purity_percent} onChange={(v) => setForm({ ...form, purity_percent: v })} placeholder="e.g. 98.72%" testid="admin-coa-purity" />
            <Field label="Research Grade" value={form.research_grade} onChange={(v) => setForm({ ...form, research_grade: v })} testid="admin-coa-grade" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-muted">Storage Conditions</Label>
            <Input value={form.storage_conditions} onChange={(e) => setForm({ ...form, storage_conditions: e.target.value })} className="mt-1" data-testid="admin-coa-storage" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-brand-muted">Internal Notes (never public)</Label>
            <Textarea value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} className="mt-1 text-xs" rows={2} data-testid="admin-coa-notes" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-brand-text select-none">
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setForm({ ...form, publish: e.target.checked })}
              data-testid="admin-coa-publish"
              className="accent-cyan-400"
            />
            Publish immediately (archives the previous current COA)
          </label>
        </div>

        <div className="flex flex-col">
          <Label className="text-xs uppercase tracking-wider text-brand-muted mb-1">Drag and drop COA file (PDF, PNG, JPG)</Label>
          <div
            data-testid="admin-coa-drop-zone"
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`flex-1 min-h-[220px] border-2 border-dashed rounded-none flex flex-col items-center justify-center text-center px-6 py-8 cursor-pointer transition-colors ${dragActive ? "border-brand-navy bg-brand-surface" : "border-brand-border bg-white/60 hover:border-slate-500"}`}
          >
            <UploadSimple size={32} weight="duotone" className={dragActive ? "text-brand-accent" : "text-brand-muted"} />
            {file ? (
              <>
                <div className="mt-3 font-mono text-sm text-brand-navy">{file.name}</div>
                <div className="text-xs text-brand-muted mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type}</div>
                <div className="mt-3 text-[11px] text-brand-muted">Click or drop to replace</div>
              </>
            ) : (
              <>
                <div className="mt-3 text-sm text-brand-text">Drop your COA file here</div>
                <div className="text-xs text-brand-muted mt-1">Accepts PDF · PNG · JPG (max 25 MB)</div>
                <div className="mt-2 text-[11px] text-brand-muted">Images will be auto-converted to PDF for download.</div>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              data-testid="admin-coa-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <Button
            onClick={submit}
            disabled={uploading}
            data-testid="admin-coa-submit"
            className="mt-4 bg-brand-navy hover:bg-brand-navy2 text-white font-semibold uppercase tracking-widest"
          >
            {uploading ? "Uploading…" : "Upload COA"}
          </Button>
        </div>
      </div>
    </section>
  );
}


function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-brand-muted">{label}</Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-9 text-sm"
        data-testid={testid}
      />
    </div>
  );
}


// -----------------------------------------------------------------------------
// Existing COAs list with filters, current/archive actions
// -----------------------------------------------------------------------------

function COAList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productFilter, setProductFilter] = useState("");
  const [lotFilter, setLotFilter] = useState("");
  const [labFilter, setLabFilter] = useState("");
  const [includeArchived, setIncludeArchived] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (productFilter) params.product_id = productFilter;
      if (lotFilter)     params.lot_number  = lotFilter;
      if (labFilter)     params.laboratory  = labFilter;
      params.include_archived = includeArchived;
      const { data } = await api.get("/admin/coa/list-rich", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [productFilter, lotFilter, labFilter, includeArchived]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("coa-refresh", h);
    return () => window.removeEventListener("coa-refresh", h);
  }, [load]);

  const setCurrent = async (id) => {
    if (!window.confirm("Publish this COA as the current verified batch? The previous current will be archived (never deleted).")) return;
    try {
      await api.post(`/admin/coa/${id}/set-current`);
      toast.success("Published — this is now the current COA");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this COA? Historical record is preserved but it will no longer be listed as current.")) return;
    try {
      await api.post(`/admin/coa/${id}/archive`);
      toast.success("Archived");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="border border-brand-border bg-white" data-testid="admin-coa-list-section">
      <div className="p-6 border-b border-brand-border flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-semibold text-brand-navy mr-auto">
          Existing Certificates ({items.length})
        </h2>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            value={lotFilter}
            onChange={(e) => setLotFilter(e.target.value)}
            data-testid="admin-coa-filter-lot"
            placeholder="Lot number…"
            className="bg-white/60 border border-brand-border pl-8 pr-3 py-1.5 text-xs w-40"
          />
        </div>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            value={labFilter}
            onChange={(e) => setLabFilter(e.target.value)}
            data-testid="admin-coa-filter-lab"
            placeholder="Laboratory…"
            className="bg-white/60 border border-brand-border pl-8 pr-3 py-1.5 text-xs w-40"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-brand-text select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="admin-coa-filter-archived"
            className="accent-cyan-400"
          />
          Include archived
        </label>
      </div>

      {loading ? (
        <div className="p-8 text-brand-muted text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-16 text-center">
          <div className="font-display text-xl text-brand-navy">No COAs match the current filters.</div>
        </div>
      ) : (
        <ul className="divide-y divide-brand-border" data-testid="admin-coa-list">
          {items.map((c) => (
            <li key={c.id} className="p-6 grid grid-cols-12 gap-4 items-center bg-white/40" data-testid={`admin-coa-row-${c.id}`}>
              <div className="col-span-4">
                <div className="font-display text-brand-navy flex items-center gap-2">
                  <Certificate size={16} weight="duotone" className="text-brand-accent" />
                  {c.product_display || c.product_id.slice(0, 6)}
                </div>
                <div className="text-[11px] font-mono text-brand-muted">
                  {c.product_sku} · {c.original_filename}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-mono text-xs text-brand-text">Lot {c.lot_number || "—"} · v{c.version}</span>
                  {c.is_current && <Badge tone="emerald">CURRENT</Badge>}
                  {c.is_archived && <Badge tone="slate">ARCHIVED</Badge>}
                </div>
              </div>
              <div className="col-span-3 text-xs text-brand-muted font-mono space-y-0.5">
                <div>{c.laboratory || "—"}</div>
                <div>Tested {c.test_date || "—"}</div>
                <div>Issued {c.issue_date || "—"}</div>
                <div>Purity {c.purity_percent || "—"}</div>
              </div>
              <div className="col-span-3 text-[11px] font-mono text-brand-muted break-all">
                {c.download_filename && <div className="text-brand-text">{c.download_filename}</div>}
                {c.sha256_hash && <div>SHA-256: {c.sha256_hash.slice(0, 24)}…</div>}
                <div>{(c.size_bytes / 1024).toFixed(1)} KB · {c.content_type}</div>
              </div>
              <div className="col-span-2 flex flex-col gap-1.5 items-end">
                {c.product_slug && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/coa/${c.product_slug}/${c.lot_number || c.id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border border-brand-border text-brand-text hover:bg-brand-surface w-full justify-center"
                    data-testid={`admin-coa-view-${c.id}`}
                  >
                    <Eye size={12} /> View
                  </a>
                )}
                {c.product_slug && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/coa/${c.product_slug}/${c.lot_number || c.id}/download`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border border-brand-border text-brand-text hover:bg-brand-surface w-full justify-center"
                    data-testid={`admin-coa-download-${c.id}`}
                  >
                    <DownloadSimple size={12} /> PDF
                  </a>
                )}
                {!c.is_current && (
                  <button
                    onClick={() => setCurrent(c.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 w-full justify-center"
                    data-testid={`admin-coa-setcurrent-${c.id}`}
                  >
                    <CheckCircle size={12} /> Set current
                  </button>
                )}
                {!c.is_archived && (
                  <button
                    onClick={() => archive(c.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono border border-brand-border text-brand-muted hover:bg-brand-surface w-full justify-center"
                    data-testid={`admin-coa-archive-${c.id}`}
                  >
                    <Archive size={12} /> Archive
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function Badge({ tone, children }) {
  const toneMap = {
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
    slate: "text-brand-muted bg-slate-500/10 border-slate-500/40",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${toneMap[tone]}`}>
      {children}
    </span>
  );
}
